import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Smartphone, Monitor, Tablet, Bot, HelpCircle, MapPin, Globe2, Users as UsersIcon } from "lucide-react";

// Icon picker for the device-breakdown row.
const deviceIcon = (kind) => {
  const k = (kind || "").toLowerCase();
  if (k === "mobile") return Smartphone;
  if (k === "desktop") return Monitor;
  if (k === "tablet") return Tablet;
  if (k === "bot") return Bot;
  return HelpCircle;
};

// Quick percentage formatter — gracefully handles total=0.
const pct = (n, total) => (total > 0 ? `${((n / total) * 100).toFixed(1)}%` : "—");

/**
 * Admin "Signups" tab — shows where new accounts are coming from
 * (geo-resolved country/region/city), what device they're on, and a raw
 * table of the most recent 100 signups for forensic-level transparency.
 *
 * Backed by `GET /api/admin/signup-analytics?days=N`.
 */
const SignupAnalytics = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/signup-analytics?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="signup-analytics-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading signup analytics…
      </div>
    );
  }
  if (!data) {
    return <Card className="p-6 text-center text-muted-foreground">Could not load signup analytics.</Card>;
  }

  const total = data.total_signups || 0;
  const knownDeviceTotal = (data.device_breakdown || [])
    .filter((d) => d.value !== "Unknown")
    .reduce((s, d) => s + d.count, 0);
  const knownGeoTotal = (data.top_countries_geo || [])
    .filter((d) => d.value !== "Unknown")
    .reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-6" data-testid="signup-analytics-panel">
      {/* Window selector + summary */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold text-[#1A1A1A]">{total.toLocaleString()}</span> new accounts in the last
        </div>
        <div className="flex gap-1" data-testid="signup-analytics-window">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`signup-window-${d}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors border ${
                days === d
                  ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                  : "bg-white text-[#1A1A1A] border-black/15 hover:bg-black/5"
              }`}
            >
              {d === 365 ? "1 year" : `${d}d`}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          {knownDeviceTotal > 0
            ? `Device + geo capture started ${new Date(data.recent_signups?.find((s) => s.signup_device && s.signup_device !== "unknown")?.created_at || Date.now()).toLocaleDateString()}.`
            : "Device + geo capture is now live — new signups from here on will be tagged."}
        </div>
      </div>

      {/* Device breakdown */}
      <Card className="p-5 bg-white border-black/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">Device breakdown</h3>
          <Badge variant="outline" className="text-xs">{knownDeviceTotal} known of {total}</Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {["mobile", "desktop", "tablet", "bot", "unknown"].map((k) => {
            const row = (data.device_breakdown || []).find((d) => d.value === k || d.value === k.charAt(0).toUpperCase() + k.slice(1));
            const count = row?.count || 0;
            const Icon = deviceIcon(k);
            return (
              <div
                key={k}
                className="p-4 rounded-lg border border-black/10 bg-[#FAF8F5]"
                data-testid={`device-card-${k}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5 text-[#C05A3C]"/>
                  <span className="text-xs text-muted-foreground">{pct(count, total)}</span>
                </div>
                <div className="font-display text-2xl">{count.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground capitalize">{k}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Top countries (geo + self-declared) side-by-side */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 bg-white border-black/10" data-testid="top-countries-geo">
          <div className="flex items-center gap-2 mb-3">
            <Globe2 className="w-4 h-4 text-[#C05A3C]"/>
            <h3 className="font-display text-lg">Top countries (geo-IP)</h3>
          </div>
          <div className="space-y-2">
            {(data.top_countries_geo || []).slice(0, 10).map((c) => (
              <div key={c.value} className="flex items-center justify-between text-sm">
                <span className={c.value === "Unknown" ? "text-muted-foreground italic" : ""}>{c.value}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1.5 bg-[#F3EFE9] rounded overflow-hidden">
                    <div className="h-full bg-[#C05A3C]" style={{ width: `${(c.count / (knownGeoTotal || 1)) * 100}%` }}/>
                  </div>
                  <span className="font-semibold w-10 text-right">{c.count}</span>
                </div>
              </div>
            ))}
            {(data.top_countries_geo || []).length === 0 && (
              <div className="text-sm text-muted-foreground">No geo data yet.</div>
            )}
          </div>
        </Card>

        <Card className="p-5 bg-white border-black/10" data-testid="top-countries-self">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon className="w-4 h-4 text-[#2A3D36]"/>
            <h3 className="font-display text-lg">Self-declared at signup</h3>
          </div>
          <div className="space-y-2">
            {(data.top_countries_self_declared || []).slice(0, 10).map((c) => (
              <div key={c.value} className="flex items-center justify-between text-sm">
                <span className={c.value === "Unknown" ? "text-muted-foreground italic" : ""}>{c.value}</span>
                <span className="font-semibold">{c.count}</span>
              </div>
            ))}
            {(data.top_countries_self_declared || []).length === 0 && (
              <div className="text-sm text-muted-foreground">No data yet.</div>
            )}
          </div>
        </Card>
      </div>

      {/* Top regions / cities */}
      <Card className="p-5 bg-white border-black/10" data-testid="top-regions">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-[#C05A3C]"/>
          <h3 className="font-display text-lg">Top regions & cities (geo-IP)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left p-2">Country</th><th className="text-left p-2">Region/State</th><th className="text-left p-2">City</th><th className="text-right p-2">Signups</th></tr>
            </thead>
            <tbody>
              {(data.top_regions || []).slice(0, 20).map((r, i) => (
                <tr key={i} className="border-t border-black/5">
                  <td className={`p-2 ${r.country === "Unknown" ? "text-muted-foreground italic" : ""}`}>{r.country}</td>
                  <td className="p-2 text-muted-foreground">{r.region || "—"}</td>
                  <td className="p-2 text-muted-foreground">{r.city || "—"}</td>
                  <td className="p-2 text-right font-semibold">{r.count}</td>
                </tr>
              ))}
              {(data.top_regions || []).length === 0 && (
                <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">No regional data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent signups (raw rows) */}
      <Card className="p-5 bg-white border-black/10" data-testid="recent-signups">
        <h3 className="font-display text-lg mb-3">Recent signups ({(data.recent_signups || []).length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Auth</th>
                <th className="text-left p-2">Device</th>
                <th className="text-left p-2">Geo (country / region / city)</th>
                <th className="text-left p-2">Self-declared</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_signups || []).map((u) => {
                const Icon = deviceIcon(u.signup_device);
                return (
                  <tr key={u.user_id} className="border-t border-black/5">
                    <td className="p-2 text-xs whitespace-nowrap">{u.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-2">
                      <div className="font-medium truncate max-w-[180px]" title={u.name}>{u.name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[180px]" title={u.email}>{u.email}</div>
                    </td>
                    <td className="p-2 capitalize text-xs">{u.auth_provider || "—"}</td>
                    <td className="p-2">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <Icon className="w-3.5 h-3.5"/> <span className="capitalize">{u.signup_device || "unknown"}</span>
                      </span>
                    </td>
                    <td className="p-2 text-xs">
                      {u.signup_country_geo ? (
                        <>
                          {u.signup_country_code && <span className="font-mono text-[10px] text-muted-foreground mr-1">{u.signup_country_code}</span>}
                          {u.signup_country_geo}
                          {u.signup_region ? <span className="text-muted-foreground"> · {u.signup_region}</span> : null}
                          {u.signup_city ? <span className="text-muted-foreground"> · {u.signup_city}</span> : null}
                        </>
                      ) : <span className="text-muted-foreground italic">—</span>}
                    </td>
                    <td className="p-2 text-xs text-muted-foreground">{u.country || "—"}</td>
                  </tr>
                );
              })}
              {(data.recent_signups || []).length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No signups in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default SignupAnalytics;
